import { useState } from "react";
import { toast } from "sonner";
import { Check, PhoneCall, Loader2, Clock } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { api, formatApiErrorDetail, trackEvent } from "../lib/api";
import { COMPANY } from "../lib/data";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Rappel() {
  const [form, setForm] = useState({ nom: "", telephone: "", creneau: "", sujet: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Merci d'accepter la politique de confidentialité."); return; }
    setLoading(true);
    try {
      await api.post("/callback", { ...form, consentement: consent });
      trackEvent("form_submit", "Rappel demandé");
      setDone(true);
      toast.success("Nous vous rappelons très vite !");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center">
        <Seo title="Demande de rappel envoyée — Carnier Toiture et Habitat" />
        <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-vegetal-100 text-vegetal-600"><Check className="h-8 w-8" /></div>
        <h1 className="mt-6 font-heading text-3xl font-bold text-neutral-900">C'est noté, on vous rappelle !</h1>
        <p className="mt-4 text-neutral-700">Un artisan de <strong>{COMPANY.name}</strong> vous contactera au créneau souhaité. Merci de votre confiance.</p>
      </div>
    );
  }

  return (
    <div>
      <Seo
        title="Être rappelé — Carnier Toiture et Habitat (Oise)"
        description="Laissez votre numéro et faites-vous rappeler par un artisan couvreur local dans l'Oise. Simple, rapide et sans engagement."
      />

      <section className="bg-vegetal-800 text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 md:py-16">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-300">Être rappelé</span>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-balance">Faites-vous rappeler gratuitement</h1>
            <p className="mt-4 max-w-2xl text-vegetal-100/80 text-lg">Pas le temps de tout écrire ? Laissez-nous simplement votre numéro, on s'occupe du reste.</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-xl mx-auto px-5 md:px-10 py-14 md:py-20">
        <Reveal>
          <form onSubmit={submit} data-testid="rappel-form" className="rounded-2xl bg-white border border-neutral-200 shadow-sm p-6 md:p-8 space-y-5">
            <div>
              <Label className="text-sm font-medium text-neutral-800">Nom complet <span className="text-terracotta-500">*</span></Label>
              <Input data-testid="rappel-nom" required value={form.nom} onChange={set("nom")} placeholder="Jean Dupont" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-sm font-medium text-neutral-800">Téléphone <span className="text-terracotta-500">*</span></Label>
              <Input data-testid="rappel-telephone" required type="tel" value={form.telephone} onChange={set("telephone")} placeholder="06 12 34 56 78" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-sm font-medium text-neutral-800">Créneau préféré</Label>
              <Select value={form.creneau} onValueChange={set("creneau")}>
                <SelectTrigger data-testid="rappel-creneau" className="mt-1.5"><SelectValue placeholder="Quand vous rappeler ?" /></SelectTrigger>
                <SelectContent>
                  {["Matin (8h-12h)", "Après-midi (12h-17h)", "Fin de journée (17h-19h)", "Peu importe"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-neutral-800">Sujet (optionnel)</Label>
              <Input data-testid="rappel-sujet" value={form.sujet} onChange={set("sujet")} placeholder="Ex : rénovation toiture, devis ravalement..." className="mt-1.5" />
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="rappel-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
              <span className="text-xs text-neutral-600 leading-relaxed">
                J'accepte d'être recontacté(e) par téléphone au sujet de ma demande. Mes données ne sont utilisées que dans ce but. Voir la{" "}
                <a href="/politique-confidentialite" className="text-terracotta-600 underline">politique de confidentialité</a>.
              </span>
            </label>

            <button data-testid="rappel-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-terracotta-500 text-white hover:bg-terracotta-600 transition-colors font-medium shadow-sm disabled:opacity-60">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi...</> : <><PhoneCall className="h-4 w-4" /> Me faire rappeler</>}
            </button>

            <p className="flex items-center justify-center gap-2 text-xs text-neutral-500">
              <Clock className="h-3.5 w-3.5" /> Réponse rapide pendant nos heures d'ouverture
            </p>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
